import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.css";
import { getMonth, getYear } from "date-fns";
import { range } from "lodash";
import { useMaterialUIController } from "context";
import {
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuX,
} from "react-icons/lu";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Custom Input trigger matching Figma Node 166:5483 (Date Picker Input Trigger)
 * - 40px default height, 10px border-radius, #cdd6d4 border, 18px calendar-days icon
 * - Entire date field is clickable to open the calendar (input text, padding, icon, and label).
 */
const CustomDateInput = forwardRef(function CustomDateInput(
  {
    value,
    onClick,
    onKeyDown,
    placeholder,
    disabled,
    readOnly,
    label,
    required,
    error,
    helperText,
    isClearable,
    onClear,
    onIconClick,
    size = "md",
    showIcon = true,
    className = "",
    isDark = false,
    id,
    name,
    // Absorb any unused legacy props
    variant,
    InputLabelProps,
    inputProps,
    fullWidth,
    ...rest
  },
  ref
) {
  const rootClass = [
    "figma-datepicker-root",
    isDark ? "figma-datepicker-root--dark" : "",
    disabled ? "figma-datepicker-root--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const containerClass = [
    "figma-datepicker__input-container",
    `figma-datepicker__input-container--${size}`,
    disabled ? "figma-datepicker__input-container--disabled" : "",
    error ? "figma-datepicker__input-container--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleTriggerClick = (e) => {
    if (disabled || readOnly) return;
    if (onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || readOnly) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onClick) onClick(e);
    } else if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div className={rootClass} id={id ? `${id}-wrapper` : undefined}>
      {label && (
        <label
          className="figma-datepicker__label"
          htmlFor={id}
          onClick={handleTriggerClick}
        >
          <span>
            {label}
            {required && <span className="figma-datepicker__required">*</span>}
          </span>
        </label>
      )}

      <div
        className={containerClass}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-haspopup="dialog"
      >
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          className="figma-datepicker__input"
          value={value || ""}
          placeholder={placeholder || "Select date"}
          disabled={disabled}
          readOnly={true}
          tabIndex={-1}
          onClick={handleTriggerClick}
          onFocus={handleTriggerClick}
          aria-invalid={Boolean(error)}
          aria-label={label || placeholder || "Select date"}
          {...rest}
        />

        <div className="figma-datepicker__actions" onClick={onIconClick}>
          {isClearable && Boolean(value) && !disabled && !readOnly && (
            <button
              type="button"
              className="figma-datepicker__clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onClear) onClear(e);
              }}
              aria-label="Clear date"
              title="Clear date"
              tabIndex={0}
            >
              <LuX size={14} />
            </button>
          )}

          {showIcon && (
            <span
              className={`figma-datepicker__icon ${
                disabled ? "figma-datepicker__icon--disabled" : ""
              }`}
            >
              <LuCalendarDays
                size={size === "sm" ? 14 : size === "lg" ? 20 : 18}
              />
            </span>
          )}
        </div>
      </div>

      {helperText && (
        <div
          className={`figma-datepicker__helper ${
            error ? "figma-datepicker__helper--error" : ""
          }`}
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

/**
 * Safely parse a value into a Date object or null
 */
function parseSafeDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Shared Figma Datepicker component (`PickDate` / `DatePicker`)
 * Matches Figma Node 140:10389 (Calendar panel 166:5380 and Input Trigger 166:5483)
 */
function PickDate({
  selected,
  onChange,
  label,
  required = false,
  disabled = false,
  disable = false, // backward compatibility
  readOnly = false,
  error = false,
  helperText,
  placeholder,
  placeholderText,
  dateFormat = "MMM d, yyyy",
  minDate,
  maxDate,
  isClearable = false,
  size = "md",
  showIcon = true,
  toggleCalendarOnIconClick = false,
  selectsRange = false,
  startDate,
  endDate,
  shouldCloseOnSelect = true,
  popperPlacement = "bottom-start",
  className = "",
  id,
  name,
  yearStart = 1950,
  yearEnd = getYear(new Date()) + 30,
  renderCustomHeader: customHeaderProp,
  showTodayButton = false,
  todayButtonLabel = "Today",
  ...rest
}) {
  let isDarkMode = false;
  try {
    const [controller] = useMaterialUIController();
    isDarkMode = Boolean(controller?.darkMode);
  } catch (err) {
    if (typeof document !== "undefined") {
      isDarkMode =
        document.documentElement.getAttribute("data-theme") === "dark" ||
        document.body.classList.contains("theme-dark") ||
        document.body.classList.contains("dark");
    }
  }

  const [headerView, setHeaderView] = useState("days"); // "days" | "picker"
  const datePickerRef = useRef(null);

  const isDisabled = Boolean(disabled || disable);
  const parsedSelected = parseSafeDate(selected);
  const parsedMinDate = parseSafeDate(minDate);
  const parsedMaxDate = parseSafeDate(maxDate);
  const parsedStartDate = parseSafeDate(startDate);
  const parsedEndDate = parseSafeDate(endDate);

  const years = useMemo(
    () => range(yearStart, Math.max(yearEnd, getYear(new Date()) + 1) + 1, 1),
    [yearStart, yearEnd]
  );

  const handleClear = useCallback(
    (e) => {
      if (onChange) {
        onChange(null, e);
      }
    },
    [onChange]
  );

  /**
   * Header matching Figma Node 166:5381 (Date Picker Navigation)
   * - < chevron button (32x32)
   * - Centered "Month Year" label (e.g. "December 2024")
   * - > chevron button (32x32)
   * - Clicking label toggles quick month/year picker
   */
  const defaultRenderCustomHeader = useCallback(
    ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => {
      const currentYear = getYear(date);
      const currentMonth = getMonth(date);

      if (headerView === "picker") {
        return (
          <div className="figma-datepicker__picker-header">
            <div className="figma-datepicker__year-row">
              <button
                type="button"
                className="figma-datepicker__nav-btn"
                onClick={() => changeYear(currentYear - 1)}
                aria-label="Previous Year"
              >
                <LuChevronLeft size={16} />
              </button>
              <select
                className="figma-datepicker__year-select"
                value={currentYear}
                onChange={({ target: { value } }) =>
                  changeYear(parseInt(value, 10))
                }
                aria-label="Select Year"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="figma-datepicker__nav-btn"
                onClick={() => changeYear(currentYear + 1)}
                aria-label="Next Year"
              >
                <LuChevronRight size={16} />
              </button>
            </div>
            <div className="figma-datepicker__months-grid">
              {MONTH_SHORT_NAMES.map((mName, mIdx) => (
                <button
                  key={mName}
                  type="button"
                  className={`figma-datepicker__month-cell${
                    mIdx === currentMonth ? " is-selected" : ""
                  }`}
                  onClick={() => {
                    changeMonth(mIdx);
                    setHeaderView("days");
                  }}
                >
                  {mName}
                </button>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div className="figma-datepicker__custom-header">
          <button
            type="button"
            className="figma-datepicker__nav-btn"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            aria-label="Previous Month"
          >
            <LuChevronLeft size={16} />
          </button>

          <button
            type="button"
            className="figma-datepicker__header-title-btn"
            onClick={() => setHeaderView("picker")}
            title="Click to jump month or year"
            aria-label={`${MONTH_NAMES[currentMonth]} ${currentYear}. Click to pick month and year`}
          >
            {MONTH_NAMES[currentMonth]} {currentYear}
          </button>

          <button
            type="button"
            className="figma-datepicker__nav-btn"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            aria-label="Next Month"
          >
            <LuChevronRight size={16} />
          </button>
        </div>
      );
    },
    [headerView, years]
  );

  const popperClasses = [
    "figma-datepicker-popper",
    isDarkMode ? "figma-datepicker-popper--dark theme-dark" : "theme-light",
  ].join(" ");

  return (
    <ReactDatePicker
      ref={datePickerRef}
      selected={parsedSelected}
      onChange={onChange}
      dateFormat={dateFormat}
      minDate={parsedMinDate}
      maxDate={parsedMaxDate}
      disabled={isDisabled}
      readOnly={readOnly}
      shouldCloseOnSelect={shouldCloseOnSelect}
      popperPlacement={popperPlacement}
      popperClassName={popperClasses}
      selectsRange={selectsRange}
      startDate={parsedStartDate}
      endDate={parsedEndDate}
      renderCustomHeader={customHeaderProp || defaultRenderCustomHeader}
      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 2)}
      onCalendarClose={() => setHeaderView("days")}
      todayButton={showTodayButton ? todayButtonLabel : undefined}
      customInput={
        <CustomDateInput
          label={label}
          required={required}
          placeholder={placeholder || placeholderText}
          disabled={isDisabled}
          readOnly={readOnly}
          error={error}
          helperText={helperText}
          isClearable={isClearable}
          onClear={handleClear}
          onIconClick={
            toggleCalendarOnIconClick
              ? (event) => {
                  event.stopPropagation();
                  if (isDisabled || readOnly) return;
                  const picker = datePickerRef.current;
                  if (picker) picker.setOpen(!picker.isCalendarOpen());
                }
              : undefined
          }
          size={size}
          showIcon={showIcon}
          className={className}
          isDark={isDarkMode}
          id={id}
          name={name}
        />
      }
      {...rest}
    />
  );
}

export { PickDate, PickDate as DatePicker };
export default PickDate;
